/**
 * Live password-policy checklist shown under the new-password field on the
 * reset screen. Reflects passwordPolicy.evaluatePassword as the user types.
 *
 * The "not common" rule is intentionally generic — it never names the org or
 * the email. An info icon after it carries the disclaimer (in a tooltip) that
 * the server may still refuse a password passing every rule here.
 */
import {useIntl} from 'react-intl';

import {evaluatePassword, PasswordRuleId} from './passwordPolicy';

interface PasswordRulesProps {
  password: string;
  email: string;
}

const RULE_LABEL_KEY: Record<PasswordRuleId, string> = {
  length: 'AUTH.NATIVE.PASSWORD.RULE.LENGTH',
  complexity: 'AUTH.NATIVE.PASSWORD.RULE.COMPLEXITY',
  notCommon: 'AUTH.NATIVE.PASSWORD.RULE.NOT_COMMON',
};

const CheckIcon = (): JSX.Element => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="3"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const DotIcon = (): JSX.Element => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="currentColor"
    stroke="none"
    aria-hidden="true"
  >
    <circle cx="12" cy="12" r="2.5" />
  </svg>
);

const InfoIcon = (): JSX.Element => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <circle cx="12" cy="12" r="10" />
    <path d="M12 16v-4M12 8h.01" />
  </svg>
);

const PasswordRules = ({password, email}: PasswordRulesProps): JSX.Element => {
  const intl = useIntl();
  const rules = evaluatePassword(password, email);
  const disclaimer = intl.formatMessage({id: 'AUTH.NATIVE.PASSWORD.DISCLAIMER'});

  return (
    <div className="password-rules">
      <ul className="password-rules-list">
        {rules.map((rule) => (
          <li key={rule.id} className={rule.satisfied ? 'password-rule is-valid' : 'password-rule'}>
            <span className="password-rule-icon">
              {rule.satisfied ? <CheckIcon /> : <DotIcon />}
            </span>
            <span>{intl.formatMessage({id: RULE_LABEL_KEY[rule.id]})}</span>
            {rule.id === 'notCommon' ? (
              <span className="password-rule-info" tabIndex={0} role="note" aria-label={disclaimer}>
                <InfoIcon />
                <span className="password-rule-tooltip" role="tooltip">
                  {disclaimer}
                </span>
              </span>
            ) : null}
          </li>
        ))}
      </ul>
    </div>
  );
};

export default PasswordRules;
